import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';

import { toast } from 'sonner';
import { fullName } from '@shared';
import { WaterlilyClient } from '@client/api/waterlily';
import { fetchClientByClientIdRequest } from '@client/model/clientModel';
import { ERROR_OR_INCOMPLETE_STATUS_TAGS } from '@client/util/constants';
import { selectClients, selectSessionAdvisor } from '@client/model/selectors';
import { fetchClientsForAdvisorByAdvisorIdRequest } from '@client/model/clientsModel';

interface UseLinkExistingPartnerOptions {
  client: Client;
  open: boolean;
  onSuccess?: () => void;
}

export function useLinkExistingPartner({ client, open, onSuccess }: UseLinkExistingPartnerOptions) {
  const clients = useSelector(selectClients);
  const advisor = useSelector(selectSessionAdvisor);
  const dispatch = useDispatch();

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelectedClient(null);
  }, [open]);

  const mutation = useMutation({
    mutationFn: (partnerClientId: string | null) =>
      WaterlilyClient.put<void>(`/client/${client.clientId}/client-partner-link`, {
        partnerClientId,
      }),
    onSuccess: () => {
      dispatch(fetchClientByClientIdRequest(client.clientId));
      if (advisor?.advisorId) {
        dispatch(
          fetchClientsForAdvisorByAdvisorIdRequest({
            advisorId: advisor.advisorId,
            isInvalidation: true,
          }),
        );
      }
      toast.success('Partner linked successfully');
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to link partner');
    },
  });

  const isAlreadyPartneredError =
    selectedClient &&
    selectedClient.partnerClientId &&
    selectedClient.partnerClientId !== client.clientId;
  const enableSave = !isAlreadyPartneredError && !!selectedClient;
  const isLastNameMismatch =
    !!selectedClient && selectedClient.clientLastName !== client.clientLastName;
  const isInitialClientDifferentFromSelectedClient = true;
  const title = `Link Partner for ${fullName(client)}`;

  function filterClients(query: string): Client[] {
    if (query === '') return [];
    return clients.filter(searchClient => {
      const hasErrorOrIncompleteStatus = searchClient.clientTags?.some(tag =>
        ERROR_OR_INCOMPLETE_STATUS_TAGS.includes(tag),
      );
      return (
        fullName(searchClient).toLowerCase().includes(query.toLowerCase()) &&
        searchClient.clientId !== client.clientId &&
        searchClient.advisorId === client.advisorId &&
        !hasErrorOrIncompleteStatus
      );
    });
  }

  function save() {
    mutation.mutate(selectedClient?.clientId ?? null);
  }

  return {
    selectedClient,
    setSelectedClient,
    isInitialClientDifferentFromSelectedClient,
    isAlreadyPartneredError,
    enableSave,
    isLastNameMismatch,
    title,
    filterClients,
    save,
    isPending: mutation.isPending,
  };
}
